import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { agentFeatures } from '../data/mockData';
import { CheckCircle, FileText, Mail, AlertTriangle, BarChart3, TrendingUp, PieChart, Users, Target, BarChart2, Award, ArrowRight } from 'lucide-react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';

const iconMap = {
  CheckCircle,
  FileText,
  Mail,
  AlertTriangle,
  BarChart3,
  TrendingUp,
  PieChart,
  Users,
  Target,
  BarChart2,
  Award
};

const VALID_ROLES = ['sales-executive', 'revenue-leader', 'account-management'];

// Each agent tab maps to a fully-routed Solutions page for deep-dive SEO content
const DEEP_DIVE_ROUTE = {
  'sales-executive': '/solutions/sales-reps',
  'revenue-leader': '/solutions/cro',
  'account-management': '/solutions/cs-manager',
};

const AgentsSection = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const urlRole = searchParams.get('role');
  const initial = urlRole && VALID_ROLES.includes(urlRole) ? urlRole : 'sales-executive';
  const [activeTab, setActiveTab] = useState(initial);

  // Keep the URL in sync when user clicks a tab
  const handleTabChange = (val) => {
    setActiveTab(val);
    // Preserve other query params; only update ?role=
    const next = new URLSearchParams(searchParams);
    next.set('role', val);
    setSearchParams(next, { replace: false });
    // Scroll the section into view so the new "page" feels like a page change
    requestAnimationFrame(() => {
      const el = document.getElementById('agents');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  // If user arrives at a URL like /?role=revenue-leader, adopt it
  useEffect(() => {
    if (urlRole && VALID_ROLES.includes(urlRole) && urlRole !== activeTab) {
      setActiveTab(urlRole);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlRole]);

  const ctaTexts = {
    'sales-executive': 'Watch the Demo',
    'revenue-leader': 'Try the MVP',
    'account-management': 'Start a Pilot'
  };

  const ctaActions = {
    'sales-executive': () => navigate('/dashboard'),
    'revenue-leader': () => navigate('/dashboard'),
    'account-management': () => window.open('https://calendar.app.google/nswB9pt6ALLZ8xoy7', '_blank'),
  };

  const featureHeaders = {
    'sales-executive': 'KEY CAPABILITIES FOR SELLERS',
    'revenue-leader': 'KEY CAPABILITIES FOR LEADERS',
    'account-management': 'KEY CAPABILITIES FOR ACCOUNT TEAMS'
  };

  return (
    <section id="agents" className="py-24 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 tracking-tight">
            Your AI Revenue Team, <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">Already Trained on 200+ CRO Conversations</span>
          </h2>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
          <TabsList className="grid w-full grid-cols-1 md:grid-cols-3 gap-2 mb-10 bg-gray-100 p-1.5 rounded-xl h-auto">
            {agentFeatures.map((agent) => (
              <TabsTrigger
                key={agent.id}
                value={agent.id}
                className="text-xs md:text-sm font-semibold py-2.5 px-4 rounded-lg data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:shadow-md data-[state=inactive]:bg-transparent data-[state=inactive]:text-gray-700 hover:bg-gray-200 transition-all duration-300"
              >
                {agent.title}
              </TabsTrigger>
            ))}
          </TabsList>

          {agentFeatures.map((agent) => (
            <TabsContent key={agent.id} value={agent.id} className="mt-0">
              <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 items-start">
                {/* LEFT: description, features, CTA */}
                <div className="lg:col-span-3 space-y-8">
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-4">
                      {agent.title}
                    </h3>
                    <p className="text-lg text-gray-600 leading-relaxed">
                      {agent.description}
                    </p>
                  </div>

                  {/* Features */}
                  <div className="space-y-4">
                    <h4 className="text-sm font-bold text-gray-500 uppercase tracking-wider">
                      {featureHeaders[agent.id]}
                    </h4>
                    {agent.features.map((feature, idx) => {
                      const Icon = iconMap[feature.icon];
                      return (
                        <div
                          key={idx}
                          className="flex items-start gap-4 p-4 rounded-xl bg-white hover:bg-blue-50 transition-all duration-300 border border-gray-100 hover:border-blue-200 cursor-pointer group"
                        >
                          <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center group-hover:bg-blue-600 transition-colors duration-300">
                            <Icon className="h-5 w-5 text-blue-600 group-hover:text-white transition-colors duration-300" />
                          </div>
                          <div>
                            <h5 className="font-semibold text-gray-900 mb-1">
                              {feature.title}
                            </h5>
                            <p className="text-sm text-gray-600">{feature.desc}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div className="flex flex-wrap items-center gap-3 pt-1">
                    <Button
                      onClick={ctaActions[agent.id]}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 text-sm font-semibold rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
                      data-testid={`agents-cta-${agent.id}`}
                    >
                      {ctaTexts[agent.id]}
                    </Button>
                    <button
                      type="button"
                      onClick={() => navigate(DEEP_DIVE_ROUTE[agent.id])}
                      className="inline-flex items-center gap-1 text-sm font-semibold text-blue-600 hover:text-blue-700 group"
                      data-testid={`agents-deep-dive-${agent.id}`}
                    >
                      Deep dive: full {agent.title} page
                      <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
                    </button>
                  </div>
                </div>

                {/* RIGHT: testimonial quote */}
                <div className="lg:col-span-2 lg:sticky lg:top-28">
                  <Card className="p-6 bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
                    <blockquote className="text-base italic text-gray-700 mb-4">
                      "{agent.testimonial.quote}"
                    </blockquote>
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 text-white flex items-center justify-center font-bold text-base shadow-sm ring-2 ring-white">
                        {agent.testimonial.author.trim().charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div className="font-semibold text-gray-900">
                          {agent.testimonial.author}
                        </div>
                        <div className="text-sm text-gray-600">
                          {agent.testimonial.role}
                        </div>
                      </div>
                    </div>
                  </Card>
                </div>
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </section>
  );
};

export default AgentsSection;
